import { assertNever } from "@p0security/shared/types/index";

import type { AgenticIntegration } from "./components.ts";

type Gateway = Pick<AgenticIntegration["gateway"][string], "domainHosting">;

/** The gateway's current public base URL, regardless of which domain-hosting
 * option is configured. Returns `undefined` if `gateway` is undefined, or
 * hasn't completed domain-hosting setup; callers scanning across every
 * installed gateway want this, since an incomplete gateway there is simply
 * skipped rather than a bug. */
export const gatewayUrlOf = (
  gateway: Gateway | undefined
): string | undefined => {
  const { domainHosting } = gateway ?? {};
  if (!domainHosting) return undefined;
  const { type } = domainHosting;
  switch (type) {
    case "selfHosted":
      return domainHosting.url;
    case "p0Hosted":
      return `https://${domainHosting.hostedDomain}`;
    default:
      return assertNever(type);
  }
};

/** Like {@link gatewayUrlOf}, but throws instead of returning `undefined` —
 * for callers operating on a single gateway that's already expected to be
 * fully installed, where a missing URL is a bug, not an expected state. */
export const checkedGatewayUrlOf = (gateway: Gateway): string => {
  const url = gatewayUrlOf(gateway);
  if (!url) {
    throw new Error("Gateway has no configured domain hosting");
  }
  return url;
};

/** The gateway's configured OAuth/JWKS issuer, or `undefined` if `gateway` is
 * undefined, or it hasn't completed domain-hosting setup. Like
 * {@link gatewayUrlOf}, this doesn't throw — callers scan across every
 * installed gateway to match a JWT issuer, where an incomplete gateway is
 * simply not a match. */
export const gatewayOauthEndpointOf = (
  gateway: Gateway | undefined
): string | undefined => {
  const { domainHosting } = gateway ?? {};
  if (!domainHosting) return undefined;
  const { type } = domainHosting;
  switch (type) {
    case "selfHosted":
      return domainHosting["oauth-endpoint"] || domainHosting.url;
    case "p0Hosted":
      // No separate oauth-endpoint concept for a P0-hosted domain — P0
      // controls the whole hostname, so the gateway's own URL is always it.
      return gatewayUrlOf(gateway);
    default:
      return assertNever(type);
  }
};

/** The gateway's configured LoadBalancer address (IP or hostname), or
 * `undefined` if `gateway` is undefined, or it hasn't completed
 * domain-hosting setup. */
export const loadBalancerIpOf = (
  gateway: Gateway | undefined
): string | undefined => {
  const { domainHosting } = gateway ?? {};
  if (!domainHosting) return undefined;
  const { type } = domainHosting;
  switch (type) {
    case "selfHosted":
    case "p0Hosted":
      return domainHosting.loadBalancerIp;
    default:
      return assertNever(type);
  }
};
